import { useContext } from "react";
import { UNSAFE_DataRouterStateContext } from "react-router";

/**
 * T775 — the leaf route id, read in a way that cannot take the page down with it.
 *
 * An error boundary is the last thing standing between a crash and a blank
 * document, so every hook it calls is a new way for it to die. `useMatches()` is
 * exactly that shape: it goes through `useDataRouterState`, which throws an
 * invariant ("useMatches must be used within a data router") rather than returning
 * nothing. In production both boundaries are always inside a data router and it
 * would never fire — but "would never fire" is a poor thing to have underneath the
 * screen a member sees when everything else has already failed.
 *
 * `useContext` cannot throw. Outside a provider it returns null and this returns
 * `undefined`, which the caller already tags as "unknown". Same value in
 * production, one fewer crash path in the fallback UI.
 *
 * Where that margin is actually collected: root's `ErrorBoundary`, which receives
 * its `error` as a real prop (it is the one module the build-time transform
 * decorates) and calls no other data-router hook. There `useMatches()` was the ONLY
 * throw path, and it took all 20 cases of the T38 boundary test down with it.
 * `RouteErrorBoundary` gains nothing here — its own `useRouteError()` throws under
 * exactly the same conditions — and uses this for symmetry, not for safety. Said
 * out loud because the earlier version of this comment implied otherwise.
 *
 * The LEAF match, not the boundary's own route: the boundary lives on the layout,
 * so its id is the same string for every dashboard error ever recorded. The leaf is
 * the one that says which screen she was on — the answer `JAVASCRIPT-REACT-3` could
 * never give.
 */
export function useLeafRouteId(): string | undefined {
  const state = useContext(UNSAFE_DataRouterStateContext);
  const matches = state?.matches;
  if (!matches || matches.length === 0) return undefined;
  return matches[matches.length - 1]?.route.id;
}
